import { Component, OnDestroy, Signal, computed, effect, inject } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { CookieConsentBannerComponent } from '@features/legal';
import { ProgressComponent } from '@shared/components/export/progress.component';
import { HeaderComponent } from '@shared/components/header/header.component';
import { LayoutComponent } from '@shared/components/layout/layout.component';
import { MenuComponent } from '@shared/components/menu/menu.component';
import { Menu } from '@shared/components/menu/models/menu';
import { RibbonComponent } from '@shared/components/ribbon/ribbon.component';
import { LToastComponent } from '@shared/components/toast';
import { AuthRouteEnforcerService } from '@shared/services/auth/auth-route-enforcer.service';
import { GlobalSpinnerService } from '@shared/services/global-spinner/global-spinner.service';
import { MeService } from '@shared/services/me/me.service';
import { PageKey } from '@shared/services/page/models/page.key';
import { ParamsService } from '@shared/services/params/params.service';
import { PermissionService } from '@shared/services/permission/permission.service';
import { SessionStateService } from '@shared/services/session/session-state.service';
import { ThemeService } from '@shared/services/theme/theme.service';
import { Subscription, filter } from 'rxjs';
import { GlobalCacheConfig } from 'ts-cacheable';
import { environment } from '../environments/environment';
import { PermissionEnum, ResourceEnum } from '../environments/shared-environment/permission';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [RouterOutlet, LayoutComponent, MenuComponent, HeaderComponent, ProgressComponent, RibbonComponent, LToastComponent, CookieConsentBannerComponent],
  template: `
      <l-layout [fullscreen]="fullscreen">
        <l-header class="block w-full h-auto" header />
        <l-menu menu [menus]="menus()" />

        <router-outlet />
      </l-layout>

      <l-toast />

      <l-ribbon />
      <l-progress />
      <l-cookie-consent-banner />
   `
})
export class AppComponent implements OnDestroy {
  private readonly authRouteEnforcer = inject(AuthRouteEnforcerService);

  private readonly sessionState = inject(SessionStateService);

  constructor(
    router: Router,
    params: ParamsService,
    route: ActivatedRoute,
    themeService: ThemeService,
    globalSpinnerService: GlobalSpinnerService,
    private readonly meService: MeService,
    private readonly permissionService: PermissionService
  ) {
    params.initialize();
    themeService.initialize();
    globalSpinnerService.initialize();

    GlobalCacheConfig.maxAge = 3600000;
    GlobalCacheConfig.maxCacheCount = 100;

    this.routerSubscription = router.events.pipe(filter(event => event instanceof NavigationEnd)).subscribe(() => {
      const currentRoute = route.root.children[0].snapshot.data;
      this.fullscreen = currentRoute['fullscreen'] || false;
      this.authRouteEnforcer.enforceCurrentRoute();
    });

    effect(() => {
      this.meService.me();
      this.sessionState.sessionRevoked();
      this.authRouteEnforcer.enforceCurrentRoute();
    });
  }

  private readonly routerSubscription?: Subscription;

  public fullscreen = false;

  public menus: Signal<Menu[]> = computed(() => {
    this.meService.me();

    const can = (resource: ResourceEnum, permission: PermissionEnum, menu: Menu | Menu[]): Menu[] => (this.permissionService.has(resource, permission) ? (Array.isArray(menu) ? menu : [menu]) : []);

    return [
      {
        icon: 'home',
        title: 'Home',
        subtitle: 'Página inicial',
        link: '/home',
        key: PageKey.HOME
      },
      ...can(ResourceEnum.COLLABORATORS, PermissionEnum.VIEW, {
        icon: 'calendar_view_week',
        title: 'Alocação',
        subtitle: 'Projetos por colaborador e CW',
        link: '/weekly-allocations',
        key: PageKey.WEEKLY_ALLOCATIONS,
        group: 'Operação'
      }),
      ...can(ResourceEnum.PROJECTS, PermissionEnum.VIEW, {
        icon: 'layers',
        title: 'Projetos',
        subtitle: 'Cadastro e custos',
        link: '/projects',
        key: PageKey.PROJECTS,
        group: 'Operação'
      }),
      ...can(ResourceEnum.PROJECTS, PermissionEnum.VIEW, {
        icon: 'analytics',
        title: 'Projetos',
        subtitle: 'Resumo financeiro e MO',
        link: '/reports/projects',
        key: PageKey.REPORTS_PROJECTS,
        group: 'Relatórios'
      }),
      ...can(ResourceEnum.PROJECTS, PermissionEnum.VIEW, {
        icon: 'calendar_view_week',
        title: 'Alocação',
        subtitle: 'Em desenvolvimento',
        link: '',
        key: PageKey.REPORTS_ALLOCATIONS,
        wip: true,
        group: 'Relatórios'
      }),
      ...can(ResourceEnum.COLLABORATORS, PermissionEnum.VIEW, {
        icon: 'person_add',
        title: 'Colaboradores',
        subtitle: 'Equipe e senioridade',
        link: '/collaborators',
        key: PageKey.COLLABORATORS,
        group: 'Sistema'
      }),
      ...can(ResourceEnum.TEAMS, PermissionEnum.VIEW, {
        icon: 'groups',
        title: 'Verticais Tecnológicas',
        subtitle: 'Divisões de time',
        link: '/teams',
        key: PageKey.TEAMS,
        group: 'Sistema'
      }),
      ...can(ResourceEnum.JOB_POSITIONS, PermissionEnum.VIEW, {
        icon: 'work',
        title: 'Cargos',
        subtitle: 'Cargos e funções',
        link: '/job-positions',
        key: PageKey.JOB_POSITIONS,
        group: 'Sistema'
      }),
      ...can(ResourceEnum.USERS, PermissionEnum.VIEW, {
        icon: 'group',
        title: 'Usuários',
        subtitle: 'Gerencie usuários',
        link: '/users',
        key: PageKey.USERS,
        group: 'Sistema'
      }),
      ...(environment.production
        ? []
        : [
            {
              icon: 'settings',
              title: 'Playground',
              subtitle: 'Design system',
              link: '/playground',
              key: PageKey.PLAYGROUND,
              group: 'Sistema'
            }
          ])
    ];
  });

  public ngOnDestroy(): void {
    this.routerSubscription?.unsubscribe();
  }
}
